import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import type { FieldSummaryData } from "../../Types/VoiceAssistant/VoiceAssistantTypes";

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function formatObjectKey(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

function renderTextBlock(value: string) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
    >
      {value}
    </Typography>
  );
}

function SubsectionList({
  items,
}: {
  items: { label: string; value: string | undefined }[];
}) {
  const visible = items.filter((item) => hasText(item.value));
  if (visible.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {visible.map((item) => (
        <Box key={item.label}>
          <Typography variant="caption" fontWeight={700} color="text.primary">
            {item.label}
          </Typography>
          {renderTextBlock(item.value!)}
        </Box>
      ))}
    </Box>
  );
}

function SummarySection({
  label,
  value,
  compact,
}: {
  label: string;
  value: unknown;
  compact?: boolean;
}) {
  if (value == null || value === "") return null;

  if (typeof value === "string") {
    if (!hasText(value)) return null;
    return (
      <Paper
        variant="outlined"
        sx={{
          p: compact ? 1.25 : 1.5,
          borderRadius: 2,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="primary.main"
          sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          {label}
        </Typography>
        <Divider sx={{ my: 0.75 }} />
        {renderTextBlock(value)}
      </Paper>
    );
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>).map(
      ([key, entryValue]) => ({
        label: formatObjectKey(key),
        value: typeof entryValue === "string" ? entryValue : undefined,
      }),
    );
    if (!entries.some((entry) => hasText(entry.value))) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: compact ? 1.25 : 1.5,
          borderRadius: 2,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="primary.main"
          sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          {label}
        </Typography>
        <Divider sx={{ my: 0.75 }} />
        <SubsectionList items={entries} />
      </Paper>
    );
  }

  return null;
}

type Props = {
  data: FieldSummaryData;
  compact?: boolean;
};

export default function FieldSummaryPanel({ data, compact = false }: Props) {
  const medicalHistory =
    typeof data.keyMedicalHistoryAndDiagnoses === "object" &&
    data.keyMedicalHistoryAndDiagnoses !== null
      ? data.keyMedicalHistoryAndDiagnoses
      : hasText(data.keyMedicalHistoryAndDiagnoses as string)
        ? { summary: data.keyMedicalHistoryAndDiagnoses as string }
        : undefined;

  const investigations =
    typeof data.investigations === "object" && data.investigations !== null
      ? data.investigations
      : hasText(data.investigations as string)
        ? { labs: data.investigations as string }
        : undefined;

  const procedures =
    typeof data.proceduresAndTreatments === "object" &&
    data.proceduresAndTreatments !== null
      ? data.proceduresAndTreatments
      : hasText(data.proceduresAndTreatments as string)
        ? { generalProcedures: data.proceduresAndTreatments as string }
        : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1.25 : 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant={compact ? "subtitle1" : "h6"} fontWeight={700}>
          AI patient summary
        </Typography>
        {data.patientId != null ? (
          <Chip
            size="small"
            label={`Patient #${data.patientId}`}
            color="primary"
            variant="outlined"
          />
        ) : null}
      </Box>

      {hasText(data.patientOverview) ? (
        <Paper
          variant="outlined"
          sx={{
            p: compact ? 1.25 : 1.5,
            borderRadius: 2,
            borderColor: "primary.light",
            bgcolor: "grey.50",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="primary.main"
            sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            Patient overview
          </Typography>
          <Divider sx={{ my: 0.75 }} />
          {renderTextBlock(data.patientOverview)}
        </Paper>
      ) : null}

      {medicalHistory ? (
        <SummarySection
          label="Key medical history & diagnoses"
          value={{
            Summary: medicalHistory.summary,
            "Chronic / recurrent issues": medicalHistory.chronicRecurrentIssues,
            "Principal diagnoses": medicalHistory.principalDiagnoses,
            "Chief complaints": medicalHistory.chiefComplaints,
          }}
          compact={compact}
        />
      ) : null}

      <SummarySection label="Allergies" value={data.allergies} compact={compact} />
      <SummarySection label="Social history" value={data.socialHistory} compact={compact} />
      <SummarySection label="Family history" value={data.familyHistory} compact={compact} />
      <SummarySection label="Vitals" value={data.vitals} compact={compact} />

      {investigations ? (
        <SummarySection
          label="Investigations"
          value={{
            Labs: investigations.labs,
            Radiology: investigations.radiology,
          }}
          compact={compact}
        />
      ) : null}

      {procedures ? (
        <SummarySection
          label="Procedures & treatments"
          value={{
            "General procedures": procedures.generalProcedures,
            "Dental procedures": procedures.dentalProcedures,
          }}
          compact={compact}
        />
      ) : null}

      <SummarySection
        label="Medications & prescription"
        value={data.medicationsPrescription}
        compact={compact}
      />
      <SummarySection label="Documents" value={data.documents} compact={compact} />
      <SummarySection
        label="Overall clinical picture"
        value={data.overallClinicalPicture}
        compact={compact}
      />
    </Box>
  );
}
